import { apiRequest } from './client'
import { readInt } from './json'

export type CatalogVersionReason = 'PRE_IMPORT' | 'PRE_BULK_DELETE' | 'PRE_RESTORE' | 'MANUAL'

export type CatalogVersionSummaryDto = {
  id: number
  scope_type: 'SUPPLIER_ORG' | 'BUYER_PRIVATE_SUPPLIER'
  organization_id: number
  buyer_owner_organization_id: number | null
  private_supplier_id: number | null
  reason: CatalogVersionReason
  source_filename: string | null
  item_count: number
  active_item_count: number
  note: string | null
  created_at: string
}

export type CatalogVersionRestoreResponse = {
  restored_version_id: number
  safety_version_id: number
  restored_item_count: number
  deactivated_item_count: number
}

const normalizeVersion = (json: Record<string, unknown>): CatalogVersionSummaryDto => ({
  id: readInt(json, 'id', 'id'),
  scope_type: String(json.scope_type ?? json.scopeType ?? 'SUPPLIER_ORG') as CatalogVersionSummaryDto['scope_type'],
  organization_id: readInt(json, 'organizationId', 'organization_id'),
  buyer_owner_organization_id:
    json.buyer_owner_organization_id != null || json.buyerOwnerOrganizationId != null
      ? readInt(json, 'buyerOwnerOrganizationId', 'buyer_owner_organization_id')
      : null,
  private_supplier_id:
    json.private_supplier_id != null || json.privateSupplierId != null
      ? readInt(json, 'privateSupplierId', 'private_supplier_id')
      : null,
  reason: String(json.reason ?? 'PRE_IMPORT') as CatalogVersionReason,
  source_filename: (json.source_filename as string | null) ?? (json.sourceFilename as string | null) ?? null,
  item_count: readInt(json, 'itemCount', 'item_count'),
  active_item_count: readInt(json, 'activeItemCount', 'active_item_count'),
  note: (json.note as string | null) ?? null,
  created_at: String(json.created_at ?? json.createdAt ?? ''),
})

export const listSupplierCatalogVersions = async (organizationId: number) => {
  const rows = await apiRequest<Record<string, unknown>[]>(
    `/operator/catalog-versions/supplier?organizationId=${organizationId}`,
  )
  return rows.map(normalizeVersion)
}

export const listBuyerPrivateSupplierCatalogVersions = async (
  organizationId: number,
  privateSupplierId: number,
) => {
  const rows = await apiRequest<Record<string, unknown>[]>(
    `/operator/catalog-versions/buyer-private-supplier?organizationId=${organizationId}&privateSupplierId=${privateSupplierId}`,
  )
  return rows.map(normalizeVersion)
}

export const restoreCatalogVersion = async (versionId: number) => {
  const json = await apiRequest<Record<string, unknown>>(
    `/operator/catalog-versions/${versionId}/restore`,
    { method: 'POST' },
  )
  return {
    restored_version_id: readInt(json, 'restoredVersionId', 'restored_version_id'),
    safety_version_id: readInt(json, 'safetyVersionId', 'safety_version_id'),
    restored_item_count: readInt(json, 'restoredItemCount', 'restored_item_count'),
    deactivated_item_count: readInt(json, 'deactivatedItemCount', 'deactivated_item_count'),
  } satisfies CatalogVersionRestoreResponse
}

export const catalogVersionReasonLabel = (reason: CatalogVersionReason): string => {
  switch (reason) {
    case 'PRE_IMPORT':
      return 'Перед импортом'
    case 'PRE_BULK_DELETE':
      return 'Перед массовым удалением'
    case 'PRE_RESTORE':
      return 'Перед откатом'
    case 'MANUAL':
      return 'Вручную'
    default:
      return reason
  }
}
